import type { FuelUnit, MissionEvent, MissionRow } from "./api";

const UNIT_ABBREV: Record<string, string> = {
  litres: "L",
  gallons: "gal",
  pounds: "lbs",
  kilograms: "kg",
};

export const unitAbbrev = (unit: string) => UNIT_ABBREV[unit] ?? unit;

/**
 * Decimal hours exactly as the app shows them: rounded to the nearest minute,
 * then to two places. 1h 55m is 1.92 — the one-decimal "1.9" this used to show
 * hid a difference from the pilot's own summary.
 */
export function decimalHours(seconds: number): string {
  const minutes = Math.max(0, Math.round(seconds / 60));
  return (Math.round((minutes / 60) * 100) / 100).toFixed(2);
}

/** The app's FuelConverter: via kilograms, at Jet A-1 density. */
export function convertFuel(value: number, from: FuelUnit, to: FuelUnit): number {
  const kg = { kilograms: value, litres: value * 0.8, gallons: value * 3.028, pounds: value / 2.20462 }[from];
  return { kilograms: kg, litres: kg / 0.8, gallons: kg / 3.028, pounds: kg * 2.20462 }[to];
}

/**
 * A fuel figure in the organisation's display unit (or the mission's own when
 * none is set), rounded to a whole number with its unit.
 */
export function amount(value: string | number | null, from: FuelUnit, display: FuelUnit = from): string {
  if (value === null) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return `${Math.round(convertFuel(n, from, display)).toLocaleString()} ${unitAbbrev(display)}`;
}

/** Average burn over engine running time — zero engine time has no rate. */
export function averageBurn(mission: MissionRow, display: FuelUnit = mission.fuelUnit): string {
  if (!mission.fuelBurnt || mission.engineTimeSeconds === 0) return "—";
  const perHour = convertFuel(Number(mission.fuelBurnt), mission.fuelUnit, display) / (mission.engineTimeSeconds / 3600);
  if (!Number.isFinite(perHour) || perHour <= 0) return "—";
  return `${Math.round(perHour).toLocaleString()} ${unitAbbrev(display)}/hr`;
}

export const EVENT_LABEL: Record<string, string> = {
  start: "Mission started",
  engineStarted: "Engine start",
  takeoff: "Takeoff",
  drop: "Drop",
  reFuel: "Refuel",
  landed: "Landed",
  engineShutdown: "Engine shutdown",
  end: "Mission ended",
};

export type Leg = {
  index: number;
  events: MissionEvent[];
  /** Takeoff to landing, or null while still airborne or when never flown. */
  airborneSeconds: number | null;
  drops: number;
};

function describeLeg(index: number, events: MissionEvent[]): Leg {
  const takeoff = events.find((e) => e.event === "takeoff");
  const landed = takeoff ? events.find((e, i) => i > events.indexOf(takeoff) && e.event === "landed") : undefined;
  const seconds =
    takeoff && landed
      ? (new Date(landed.recordedAt).getTime() - new Date(takeoff.recordedAt).getTime()) / 1000
      : null;
  return {
    index,
    events,
    airborneSeconds: seconds !== null && seconds > 0 ? Math.round(seconds) : null,
    drops: events.filter((e) => e.event === "drop").length,
  };
}

/**
 * A line-for-line port of the app's `splitIntoLegs`, so the dashboard groups a
 * mission exactly as the pilot sees it on the iPad.
 *
 * Every event belongs to a leg and the order is never changed. A leg runs from
 * its preflight through takeoff and landing, and keeps the ground work after
 * landing — a refuel, a shutdown — until the next takeoff. If the engine was
 * shut down and started again between two flights, the restart opens the next
 * leg, and any on/off toggles in between are left out, as the app does.
 */
export function splitIntoLegs(events: MissionEvent[]): Leg[] {
  const count = events.length;
  if (count === 0) return [];

  const firstFrom = (from: number, is: (e: MissionEvent) => boolean) => {
    for (let i = from; i < count; i++) if (is(events[i]!)) return i;
    return null;
  };
  const firstIn = (lo: number, hi: number, is: (e: MissionEvent) => boolean) => {
    for (let i = lo; i < hi; i++) if (is(events[i]!)) return i;
    return null;
  };
  const lastIn = (lo: number, hi: number, is: (e: MissionEvent) => boolean) => {
    for (let i = hi - 1; i >= lo; i--) if (is(events[i]!)) return i;
    return null;
  };

  const legs: MissionEvent[][] = [];
  let legStart = 0;

  while (legStart < count) {
    const takeoff = firstFrom(legStart, (e) => e.event === "takeoff");
    if (takeoff === null) {
      // No more flights: whatever remains becomes the final leg.
      legs.push(events.slice(legStart));
      break;
    }

    const landed = firstFrom(takeoff + 1, (e) => e.event === "landed");
    if (landed === null) {
      // Airborne now — the mission is still in progress.
      legs.push(events.slice(legStart));
      break;
    }

    const nextTakeoff = firstFrom(landed + 1, (e) => e.event === "takeoff") ?? count;

    const firstShutdown = firstIn(landed + 1, nextTakeoff, (e) => e.event === "engineShutdown");
    const lastStart = lastIn(landed + 1, nextTakeoff, (e) => e.event === "engineStarted");

    if (firstShutdown !== null && lastStart !== null && firstShutdown < lastStart) {
      const leg: MissionEvent[] = [];
      for (let i = legStart; i < lastStart; i++) {
        const e = events[i]!;
        const toggle = e.event === "engineShutdown" || e.event === "engineStarted";
        if (i > firstShutdown && i < lastStart && toggle) continue;
        leg.push(e);
      }
      legs.push(leg);
      legStart = lastStart; // The restart is the next leg's preflight.
      continue;
    }

    legs.push(events.slice(legStart, nextTakeoff));
    legStart = nextTakeoff;
  }

  return legs.map((leg, i) => describeLeg(i + 1, leg));
}

export function timeOfDay(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function dayAndMonth(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** "just now", "4 min ago", "2 hr ago", "3 days ago" — for at-a-glance lists. */
export function relativeTime(iso: string, now = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

/** Minutes since a moment, for "flying 52 min" style durations. */
export function minutesSince(iso: string, now = Date.now()): number {
  return Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
}

/** A duration in plain words: "52 min", "2 hr 5 min". */
export function duration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

/** Suburb when known, otherwise coordinates — the same fallback the app uses. */
export function placeName(lat: number | null, lon: number | null, suburb: string | null): string | null {
  if (suburb) return suburb;
  if (lat === null || lon === null) return null;
  return `${lat.toFixed(4)}°, ${lon.toFixed(4)}°`;
}

/** What a mission is doing right now, in words, from the app's phase. */
export const PHASE_LABEL: Record<string, string> = {
  missionStarted: "Setting up",
  engineOn: "Engine running",
  inFlight: "Airborne",
  landed: "Landed",
  engineOff: "Engine off",
  missionEnded: "Ended",
};
